import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { fetchAuth } from '../../../services/apiClient';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 6px 32px rgba(0, 0, 0, 0.18);
  width: 100%;
  max-width: 920px;
  max-height: 92vh;
  overflow-y: auto;
  padding: 28px 28px 24px;

  @media (max-width: 640px) {
    padding: 20px 16px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  color: #34574a;
  font-size: 1.25rem;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 20px;
  color: #777;
  font-size: 0.875rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  &.cols3 {
    grid-template-columns: repeat(3, 1fr);
    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.full {
    grid-column: 1 / -1;
  }

  label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #555;

    &.required::after {
      content: ' *';
      color: #c62828;
    }
  }

  input,
  select,
  textarea {
    padding: 10px 12px;
    border: 2px solid ${props => (props.$highlight ? '#ba824f' : '#eee')};
    border-radius: 8px;
    font-size: 0.875rem;
    font-family: inherit;
    width: 100%;
    box-sizing: border-box;

    &:focus {
      border-color: #ba824f;
      outline: none;
    }

    &:disabled {
      background: #f5f5f5;
      color: #999;
    }
  }

  textarea {
    resize: vertical;
    min-height: 96px;
  }
`;

const PrimaryButton = styled.button`
  background: #34574a;
  color: #fff;
  border: none;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: #2a4539;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GoldButton = styled(PrimaryButton)`
  background: #ba824f;
  &:hover {
    background: #a77445;
  }
`;

const GhostButton = styled.button`
  background: #fff;
  color: #666;
  border: 1px solid #eee;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover {
    background: #f8f9fa;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const ErrorBox = styled.div`
  background: #ffebee;
  border: 1px solid #f1d6da;
  color: #c62828;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-top: 16px;
  white-space: pre-wrap;
`;

const WarnBox = styled.div`
  background: #fff8e1;
  border: 1px solid #f0dfa8;
  color: #8a6d1d;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-top: 16px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border: 4px solid #eee;
  border-top-color: #34574a;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 40px auto;
`;

const Section = styled.div`
  margin-top: 22px;

  h4 {
    margin: 0 0 10px;
    color: #34574a;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 700;
  }
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  border-radius: 8px;

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: 6px;
    display: block;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PhotoWrap = styled.div`
  position: relative;

  .capa {
    position: absolute;
    top: 6px;
    left: 6px;
    background: #ba824f;
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f3f6f5;
  border: 1px solid #e3eae7;
  color: #34574a;
  border-radius: 14px;
  padding: 4px 10px;
  font-size: 0.75rem;
  margin: 0 6px 6px 0;
`;

const HostCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #f8faf9;
  border: 1px solid #e6ecea;
  border-radius: 10px;
  padding: 14px;

  img {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  color: #555;
  font-size: 0.85rem;

  b {
    color: #34574a;
  }
`;

const ResultBox = styled.div`
  text-align: center;
  padding: 12px 0 8px;

  .ok-icon {
    font-size: 46px;
    margin-bottom: 10px;
  }

  p {
    color: #555;
    font-size: 0.95rem;
    line-height: 1.7;
  }

  code {
    background: #f3f6f5;
    padding: 2px 8px;
    border-radius: 6px;
    color: #34574a;
    font-weight: 700;
  }
`;

const CloseX = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  line-height: 1;
  &:hover {
    color: #333;
  }
`;

const HeaderRow = styled.div`
  position: relative;
`;

const fmtMoney = (v, currency) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'EUR' }).format(n);
  } catch {
    return `${currency || 'EUR'} ${n.toFixed(2)}`;
  }
};

const asNumber = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const fmtData = (iso) => {
  if (!iso) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}`;
};

const ImportListingModal = ({ onClose, onImported }) => {
  const [step, setStep] = useState('form'); // form | loading | preview | confirming | done | duplicate
  const [error, setError] = useState('');

  const [url, setUrl] = useState('');
  const [checkIn, setCheckIn] = useState(plusDays(30));
  const [checkOut, setCheckOut] = useState(plusDays(35));
  const [adults, setAdults] = useState(1);
  const [currency, setCurrency] = useState('BRL');
  const [useBrowser, setUseBrowser] = useState(false);

  const [dados, setDados] = useState(null); // objeto preview (recebido da API, com edições do usuário)
  const [requiresReview, setRequiresReview] = useState(false);
  const [result, setResult] = useState(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // selects
  const [tipos, setTipos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [imobiliarias, setImobiliarias] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [moedas, setMoedas] = useState([]);

  const [tipoIMOVEL, setTipoIMOVEL] = useState('');
  const [categoriaIMOVEL, setCategoriaIMOVEL] = useState('');
  const [imobiliariaId, setImobiliariaId] = useState('');
  const [agenteId, setAgenteId] = useState('');
  const [planoIMOVEL, setPlanoIMOVEL] = useState('');

  // overrides do confirm
  const [numero, setNumero] = useState('S/N');
  const [cep, setCep] = useState('');
  const [metragem, setMetragem] = useState('');
  const [checkinTime, setCheckinTime] = useState('15:00');
  const [checkoutTime, setCheckoutTime] = useState('11:00');
  const [permMin, setPermMin] = useState('1');
  const [permMax, setPermMax] = useState('365');
  const [exibirPreco, setExibirPreco] = useState(true);

  // campos de preço
  const [valorTotal, setValorTotal] = useState('');
  const [noites, setNoites] = useState('');
  const [valorNoite, setValorNoite] = useState('');

  // operação e valor (Imovirtual: venda/arrendamento; Airbnb: temporada)
  const [operacao, setOperacao] = useState('temporada');
  const [periodicidade, setPeriodicidade] = useState('noite');
  const [moedaAnuncio, setMoedaAnuncio] = useState('BRL');

  useEffect(() => {
    const load = async () => {
      try {
        const { res, data } = await fetchAuth('/api/tiposimovel');
        if (res.ok && Array.isArray(data)) setTipos(data);
      } catch { /* ignora */ }
      try {
        const { res, data } = await fetchAuth('/api/categorias');
        if (res.ok && Array.isArray(data)) setCategorias(data);
      } catch { /* ignora */ }
      try {
        const { res, data } = await fetchAuth('/api/moedas');
        if (res.ok) setMoedas(Array.isArray(data) ? data : (data?.moedas || data?.Registros || []));
      } catch { /* ignora */ }
      try {
        const { res, data } = await fetchAuth('/api/imobiliarias/me');
        if (res.ok) setImobiliarias(Array.isArray(data) ? data : []);
      } catch { /* ignora */ }
      try {
        const { res, data } = await fetchAuth('/api/agentes/me');
        if (res.ok) setAgentes(Array.isArray(data) ? data : []);
      } catch { /* ignora */ }
      try {
        const { res, data } = await fetchAuth('/api/checkouts');
        if (res.ok) {
          const list = Array.isArray(data) ? data : (data?.checkouts || []);
          const now = Date.now();
          const ativos = list.filter((c) => {
            const aq = new Date(c.dataCHECKOUT);
            const val = new Date(aq);
            val.setDate(val.getDate() + Number(c.periodoCHECKOUT || 0));
            return Number(c.ativoCHECKOUT) === 1 && val.getTime() > now;
          });
          setPlanos(ativos);
        }
      } catch { /* ignora */ }
    };
    load();
  }, []);

  const categoriasFiltradas = useMemo(
    () => (tipoIMOVEL ? categorias.filter((c) => String(c.tipoCATEGORIA) === String(tipoIMOVEL)) : categorias),
    [categorias, tipoIMOVEL]
  );

  // fonte detectada pelo hostname da URL digitada
  const fonteDetectada = useMemo(() => {
    if (!url.trim()) return null;
    try {
      const h = new URL(url.trim()).hostname;
      if (/airbnb\.|(^|\.)abnb\.me$/i.test(h)) return 'Airbnb';
      if (/imovirtual\.com/i.test(h)) return 'Imovirtual';
      if (/casayes\./i.test(h)) return 'CasaYes';
      if (/idealista\./i.test(h)) return 'Idealista';
    } catch { /* url incompleta */ }
    return 'Fonte não reconhecida';
  }, [url]);

  const regexTipoPorOperacao = (op) => (op === 'venda' ? /compr|vend|sell/i
    : op === 'arrendamento' ? /arrend|alug|rent/i
    : /f[eé]rias|temporada|vacation|holiday/i);

  const tipoParaOperacao = (op) => {
    const alvo = regexTipoPorOperacao(op);
    const sel = tipos.find((tp) => alvo.test(`${tp.slugTIPOIMOVEL || ''} ${tp.nomeTIPOIMOVEL || tp.nome || ''}`));
    return sel ? String(sel.codigoTIPOIMOVEL) : (tipos[0] ? String(tipos[0].codigoTIPOIMOVEL) : '');
  };

  const aplicarPreview = (preview, reqReview) => {
    console.log('[ImportListingModal] preview recebido:', preview);
    setDados(preview);
    setRequiresReview(reqReview === true);
    setValorTotal(preview?.price?.total != null ? String(preview.price.total) : '');
    setNoites(preview?.price?.nights != null ? String(preview.price.nights) : '');

    const per = preview?.periodicidade || preview?.labelvalor || preview?.price?.label || 'noite';
    setValorNoite(preview?.valor != null ? String(preview.valor)
      : preview?.price?.nightly != null ? String(preview.price.nightly)
      : per !== 'noite' && preview?.price?.total != null ? String(preview.price.total) : '');

    const op = preview?.operacao || preview?.operation || 'temporada';
    setOperacao(op);
    setPeriodicidade(per);
    setMoedaAnuncio(preview?.moeda || preview?.price?.currency || currency);
    if (preview?.area != null) setMetragem(String(preview.area));

    // tipo de imóvel a partir da operação (férias/arrendar/comprar)
    const tipoSel = tipoParaOperacao(op);
    setTipoIMOVEL(tipoSel);

    const catDaLista = categorias.find((c) => String(c.tipoCATEGORIA) === String(tipoSel)) || categorias[0];
    setCategoriaIMOVEL(catDaLista ? String(catDaLista.codigoCATEGORIA) : '');

    const primeiro = planos[0];
    const planoId = primeiro?.plano?.codigoPLANO || primeiro?.planoCHECKOUT || '';
    setPlanoIMOVEL(String(planoId));

    setImobiliariaId('');
    setAgenteId('');
    setStep('preview');
  };

  const buscarDados = async () => {
    if (!url.trim()) {
      setError('Informe a URL do anúncio.');
      return;
    }
    setError('');
    setStep('loading');
    // modo detalhado leva 10-30s — timeout de 90s no request
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 90000);
    try {
      const { res, data } = await fetchAuth('/api/importacoes/anuncio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'preview', url: url.trim(), checkIn, checkOut, adults: Number(adults) || 1, currency, useBrowser }),
        signal: controller.signal,
      });
      if (!res.ok || !data?.preview) {
        throw new Error(data?.error || data?.message || 'Não foi possível ler o link do anúncio.');
      }
      aplicarPreview(data.preview, data.requiresReview);
    } catch (e) {
      setError(
        e?.name === 'AbortError'
          ? 'Tempo esgotado ao buscar o anúncio. Tente novamente.'
          : (e?.message || 'Erro ao ler o link. Verifique a URL e tente novamente.')
      );
      setStep('form');
    } finally {
      clearTimeout(timer);
    }
  };

  const recalcDiaria = (total, nights) => {
    const t = asNumber(total);
    const n = asNumber(nights);
    if (t != null && n != null && n > 0) {
      setValorNoite((t / n).toFixed(2));
    }
  };

  const updateDados = (field, value) => setDados((prev) => ({ ...prev, [field]: value }));
  const updateLocation = (field, value) =>
    setDados((prev) => ({ ...prev, location: { ...(prev?.location || {}), [field]: value } }));

  const precoOk = (asNumber(valorNoite) || 0) > 0
    || (periodicidade === 'noite' && (asNumber(valorTotal) || 0) > 0);

  const confirmar = async () => {
    const vNoite = asNumber(valorNoite)
      ?? (periodicidade === 'noite' && asNumber(valorTotal) && asNumber(noites)
        ? asNumber(valorTotal) / asNumber(noites) : null);
    if (!(vNoite > 0)) {
      setError(periodicidade === 'noite'
        ? 'Informe o valor total ou o valor da diária para continuar.'
        : 'Informe o valor do anúncio para continuar.');
      return;
    }
    if (!categoriaIMOVEL || !tipoIMOVEL) {
      setError('Selecione a categoria e o tipo do imóvel.');
      return;
    }
    setError('');
    setStep('confirming');
    try {
      const payload = {
        mode: 'confirm',
        url: dados?.sourceUrl || url.trim(),
        dados, // objeto preview completo (com edições do usuário)
        valorNoite: periodicidade === 'noite' ? Number(vNoite.toFixed(2)) : null,
        valor: Number(vNoite.toFixed(2)),
        moeda: moedaAnuncio,
        labelvalor: periodicidade,
        operacao,
        capacidade: Number(dados?.capacity) || undefined,
        quartos: Number(dados?.bedrooms) || undefined,
        endereco: dados?.address || undefined,
        numero: numero || undefined,
        cep: cep || undefined,
        latitude: dados?.latitude || undefined,
        longitude: dados?.longitude || undefined,
        metragem: metragem ? Number(metragem) : undefined,
        checkinTime: checkinTime ? `${checkinTime}:00` : undefined,
        checkoutTime: checkoutTime ? `${checkoutTime}:00` : undefined,
        permanenciaminima: permMin ? Number(permMin) : undefined,
        permanenciamaxima: permMax ? Number(permMax) : undefined,
        imobiliariaId: imobiliariaId ? Number(imobiliariaId) : null,
        agenteId: agenteId ? Number(agenteId) : null,
        categoriaIMOVEL: Number(categoriaIMOVEL),
        tipoIMOVEL: Number(tipoIMOVEL),
        planoIMOVEL: planoIMOVEL ? Number(planoIMOVEL) : null,
        exibirPreco: exibirPreco ? 1 : 0,
        currency: moeda,
      };
      const { res, data } = await fetchAuth('/api/importacoes/anuncio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) {
        setDuplicateInfo({ codigoIMOVEL: data?.codigoIMOVEL });
        setStep('duplicate');
        return;
      }
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || data?.message || 'Erro ao importar o anúncio.');
      }
      setResult(data);
      setStep('done');
      if (onImported) onImported(data);
    } catch (e) {
      setError(e?.message || 'Erro ao confirmar a importação.');
      setStep('preview');
    }
  };

  const verAnuncio = () => {
    const id = result?.imovel?.codigoIMOVEL || duplicateInfo?.codigoIMOVEL;
    if (id) window.location.href = `/dashboard/edit-listing/${id}`;
  };

  const camposBloqueados = step === 'loading' || step === 'confirming';
  // moeda exibida: a editável da revisão (default = detectada no anúncio)
  const moeda = moedaAnuncio || dados?.price?.currency || currency;
  const simboloMoeda = moedas.find((m) => m.isoMOEDA === moeda)?.simboloMOEDA || moeda;
  const host = dados?.host || {};

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget && !camposBloqueados) onClose(); }}>
      <ModalBox>
        <HeaderRow>
          <ModalTitle>Importar anúncio por link</ModalTitle>
          <ModalSubtitle>
            {step === 'form' && 'Cole o link do anúncio (Airbnb, Imovirtual, CasaYes ou Idealista) para buscar os dados.'}
            {step === 'loading' && 'Lendo os dados do anúncio…'}
            {step === 'preview' && 'Confira a prévia, ajuste os campos e confirme a publicação.'}
            {step === 'confirming' && 'Importando fotos e publicando o anúncio…'}
            {step === 'done' && 'Importação concluída.'}
            {step === 'duplicate' && 'Este anúncio já foi importado.'}
          </ModalSubtitle>
          {!camposBloqueados && <CloseX onClick={onClose} aria-label="Fechar">×</CloseX>}
        </HeaderRow>

        {step === 'form' && (
          <>
            <FieldGrid>
              <Field className="full">
                <label>URL do anúncio</label>
                <input
                  type="url"
                  placeholder="https://www.airbnb.com.br/rooms/... · https://www.imovirtual.com/pt/anuncio/... · https://casayes.pt/... · https://www.idealista.com/pt/inmueble/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
                {fonteDetectada && (
                  <span style={{ fontSize: '0.78rem', color: fonteDetectada === 'Fonte não reconhecida' ? '#c62828' : '#34574a' }}>
                    Fonte: <b>{fonteDetectada}</b>
                  </span>
                )}
              </Field>
              {/* datas/hóspedes só fazem sentido em anúncios de temporada (Airbnb) */}
              {!['Imovirtual', 'CasaYes', 'Idealista'].includes(fonteDetectada) && (
                <>
                  <Field>
                    <label>Data de entrada</label>
                    <input type="date" min={today()} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
                  </Field>
                  <Field>
                    <label>Data de saída</label>
                    <input type="date" min={checkIn || today()} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
                  </Field>
                  <Field>
                    <label>Quantidade de hóspedes</label>
                    <input type="number" min="1" value={adults} onChange={(e) => setAdults(e.target.value)} />
                  </Field>
                </>
              )}
              <Field>
                <label>Moeda</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {!moedas.length && <option value="BRL">BRL — Real brasileiro</option>}
                  {moedas.map((m) => (
                    <option key={m.codigoMOEDA} value={m.isoMOEDA}>
                      {m.isoMOEDA} — {m.simboloMOEDA || m.tipoMOEDA || m.nomeMOEDA}
                    </option>
                  ))}
                </select>
              </Field>
              <Field className="full">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={useBrowser}
                    onChange={(e) => setUseBrowser(e.target.checked)}
                  />
                  Busca detalhada — captura o preço da página (pode levar ~30s)
                </label>
              </Field>
            </FieldGrid>
            {error && <ErrorBox>{error}</ErrorBox>}
            <ButtonRow>
              <GhostButton onClick={onClose}>Cancelar</GhostButton>
              <PrimaryButton onClick={buscarDados}>Buscar dados</PrimaryButton>
            </ButtonRow>
          </>
        )}

        {(step === 'loading' || step === 'confirming') && (
          <>
            <Spinner />
            <p style={{ textAlign: 'center', color: '#777', fontSize: '0.875rem' }}>
              {step === 'loading'
                ? (useBrowser ? `Renderizando página do ${fonteDetectada || 'anúncio'}...` : 'Buscando anúncio...')
                : 'Importando fotos… isso pode levar até um minuto.'}
            </p>
          </>
        )}

        {step === 'preview' && dados && (
          <>
            {/* Galeria — primeira foto é a capa */}
            {(dados.photoPreviews?.length > 0 || dados.photos?.length > 0) ? (
              <Section>
                <h4>Fotos ({(dados.photoPreviews || dados.photos).length})</h4>
                <PhotoGrid>
                  {(dados.photoPreviews || dados.photos).map((foto, i) => {
                    const src = typeof foto === 'string' ? foto : foto.url;
                    return (
                      <PhotoWrap key={i}>
                        <img src={src} alt={`Foto ${i + 1}`} loading="lazy" />
                        {i === 0 && <span className="capa">Capa</span>}
                      </PhotoWrap>
                    );
                  })}
                </PhotoGrid>
              </Section>
            ) : (
              <WarnBox>Fotos indisponíveis para este anúncio.</WarnBox>
            )}

            {/* Chips informativos */}
            <Section>
              <SummaryRow>
                {(dados.fonteLabel || dados.source) && (
                  <span>🔗 Fonte: <b>{dados.fonteLabel || dados.source}</b></span>
                )}
                {dados.propertyType && <span>🏠 <b>{dados.propertyType}</b></span>}
                {dados.tipologia && <span>Tipologia: <b>{dados.tipologia}</b></span>}
                {dados.area != null && <span>Área: <b>{dados.area} m²</b></span>}
                {(dados.location?.city || dados.location?.state || dados.location?.country) && (
                  <span>📍 {[dados.location.city, dados.location.state, dados.location.country].filter(Boolean).join(', ')}</span>
                )}
                {dados.capacity != null && <span>👥 até <b>{dados.capacity}</b> hóspedes</span>}
                {dados.bedrooms != null && <span>🛏 <b>{dados.bedrooms}</b> quarto(s)</span>}
                {dados.beds != null && <span><b>{dados.beds}</b> cama(s)</span>}
                {dados.baths != null && <span>🚿 <b>{dados.baths}</b> banheiro(s)</span>}
                {dados.externalId && <span>ID: <b>{dados.externalId}</b></span>}
                {dados.capturedWith && (
                  <span>⚡ <b>{dados.capturedWith === 'browser' ? 'Busca detalhada' : 'Busca rápida'}</b></span>
                )}
              </SummaryRow>
              {dados.browserError && (
                <WarnBox style={{ marginTop: 8 }}>
                  A busca detalhada falhou ({dados.browserError}) — foi usada a busca rápida.
                </WarnBox>
              )}
            </Section>

            {/* Anfitrião */}
            {host.name && (
              <Section>
                <h4>Anfitrião</h4>
                <HostCard>
                  {host.profileImage && <img src={host.profileImage} alt={host.name} />}
                  <div>
                    <div style={{ fontWeight: 700, color: '#34574a' }}>
                      {host.name}
                      {host.superhost && (
                        <Chip style={{ marginLeft: 8, background: '#ba824f', color: '#fff', border: 'none' }}>Superhost</Chip>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 2 }}>
                      {[
                        host.hostingSince ? `${host.hostingSince} hospedando` : null,
                        host.rating ? `★ ${host.rating}` : null,
                        host.reviewCount ? `${host.reviewCount} avaliações` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </HostCard>
              </Section>
            )}

            {/* Comodidades */}
            {dados.amenities?.length > 0 && (
              <Section>
                <h4>Comodidades ({dados.amenities.length})</h4>
                <div>
                  {dados.amenities.map((c, i) => <Chip key={i}>{c}</Chip>)}
                </div>
              </Section>
            )}

            {/* Dados do anúncio — editáveis */}
            <Section>
              <h4>Dados do anúncio</h4>
              <FieldGrid>
                <Field className="full">
                  <label>Título</label>
                  <input value={dados.title || ''} onChange={(e) => updateDados('title', e.target.value)} />
                </Field>
                <Field className="full">
                  <label>Descrição</label>
                  <textarea rows={5} value={dados.description || ''} onChange={(e) => updateDados('description', e.target.value)} />
                </Field>
                <Field className="full">
                  <label>Endereço</label>
                  <input value={dados.address || ''} onChange={(e) => updateDados('address', e.target.value)} />
                </Field>
                <Field>
                  <label>Número</label>
                  <input value={numero} onChange={(e) => setNumero(e.target.value)} />
                </Field>
                <Field>
                  <label>CEP</label>
                  <input value={cep} onChange={(e) => setCep(e.target.value)} placeholder="11400-000" />
                </Field>
                <Field>
                  <label>Cidade</label>
                  <input value={dados.location?.city || ''} onChange={(e) => updateLocation('city', e.target.value)} />
                </Field>
                <Field>
                  <label>Estado</label>
                  <input value={dados.location?.state || ''} onChange={(e) => updateLocation('state', e.target.value)} />
                </Field>
                <Field>
                  <label>Capacidade (hóspedes)</label>
                  <input type="number" min="1" value={dados.capacity ?? ''} onChange={(e) => updateDados('capacity', e.target.value)} />
                </Field>
                <Field>
                  <label>Quartos</label>
                  <input type="number" min="0" value={dados.bedrooms ?? ''} onChange={(e) => updateDados('bedrooms', e.target.value)} />
                </Field>
                <Field>
                  <label>Metragem (m²)</label>
                  <input type="number" min="0" value={metragem} onChange={(e) => setMetragem(e.target.value)} />
                </Field>
                <Field>
                  <label>Check-in</label>
                  <input type="time" value={checkinTime} onChange={(e) => setCheckinTime(e.target.value)} />
                </Field>
                <Field>
                  <label>Check-out</label>
                  <input type="time" value={checkoutTime} onChange={(e) => setCheckoutTime(e.target.value)} />
                </Field>
                <Field>
                  <label>Permanência mínima (noites)</label>
                  <input type="number" min="1" value={permMin} onChange={(e) => setPermMin(e.target.value)} />
                </Field>
                <Field>
                  <label>Permanência máxima (noites)</label>
                  <input type="number" min="1" value={permMax} onChange={(e) => setPermMax(e.target.value)} />
                </Field>
              </FieldGrid>
            </Section>

            {/* Classificação */}
            <Section>
              <h4>Classificação</h4>
              <FieldGrid>
                <Field>
                  <label>Tipo do imóvel</label>
                  <select value={tipoIMOVEL} onChange={(e) => {
                    setTipoIMOVEL(e.target.value);
                    const cat = categorias.find((c) => String(c.tipoCATEGORIA) === String(e.target.value));
                    if (cat) setCategoriaIMOVEL(String(cat.codigoCATEGORIA));
                  }}>
                    <option value="">Selecione…</option>
                    {tipos.map((tp) => (
                      <option key={tp.codigoTIPOIMOVEL} value={tp.codigoTIPOIMOVEL}>
                        {tp.nomeTIPOIMOVEL || tp.nome}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>Categoria</label>
                  <select value={categoriaIMOVEL} onChange={(e) => setCategoriaIMOVEL(e.target.value)}>
                    <option value="">Selecione…</option>
                    {categoriasFiltradas.map((c) => (
                      <option key={c.codigoCATEGORIA} value={c.codigoCATEGORIA}>
                        {c.nomeCATEGORIA || c.nome}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>Imobiliária</label>
                  <select value={imobiliariaId} onChange={(e) => setImobiliariaId(e.target.value)}>
                    <option value="">Automática (criar a partir do anfitrião)</option>
                    {imobiliarias.map((im) => (
                      <option key={im.codigoIMOBILIARIA} value={im.codigoIMOBILIARIA}>
                        {im.nomeIMOBILIARIA}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>Agente / Anfitrião</label>
                  <select value={agenteId} onChange={(e) => setAgenteId(e.target.value)}>
                    <option value="">
                      {host.name ? `Anfitrião importado (${host.name})` : 'Automático (anfitrião do anúncio)'}
                    </option>
                    {agentes.map((a) => (
                      <option key={a.codigoAGENTE} value={a.codigoAGENTE}>
                        {a.nomeAGENTE}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>Plano / Pacote</label>
                  <select value={planoIMOVEL} onChange={(e) => setPlanoIMOVEL(e.target.value)}>
                    <option value="">Selecione…</option>
                    {planos.map((p) => {
                      const pid = p?.plano?.codigoPLANO || p?.planoCHECKOUT;
                      return (
                        <option key={p.codigoCHECKOUT} value={pid}>
                          {p.nomeCHECKOUT || `Pacote #${p.codigoCHECKOUT}`}
                        </option>
                      );
                    })}
                  </select>
                </Field>
                <Field>
                  <label>Exibir preço</label>
                  <select value={exibirPreco ? '1' : '0'} onChange={(e) => setExibirPreco(e.target.value === '1')}>
                    <option value="1">Sim</option>
                    <option value="0">Não</option>
                  </select>
                </Field>
              </FieldGrid>
            </Section>

            {/* Operação e preço */}
            <Section>
              <h4>Operação e preço</h4>
              <FieldGrid className="cols3" style={{ marginBottom: 14 }}>
                <Field>
                  <label>Operação</label>
                  <select
                    value={operacao}
                    onChange={(e) => {
                      const op = e.target.value;
                      setOperacao(op);
                      const t = tipoParaOperacao(op);
                      if (t) {
                        setTipoIMOVEL(t);
                        const cat = categorias.find((c) => String(c.tipoCATEGORIA) === String(t));
                        if (cat) setCategoriaIMOVEL(String(cat.codigoCATEGORIA));
                      }
                      if (op === 'arrendamento') setPeriodicidade('mês');
                      else if (op === 'venda') setPeriodicidade('total');
                      else setPeriodicidade('noite');
                    }}
                  >
                    <option value="temporada">Temporada</option>
                    <option value="arrendamento">Arrendamento</option>
                    <option value="venda">Venda</option>
                  </select>
                </Field>
                <Field>
                  <label>Periodicidade</label>
                  <select value={periodicidade} onChange={(e) => setPeriodicidade(e.target.value)}>
                    <option value="noite">Noite</option>
                    <option value="mês">Mês</option>
                    <option value="total">Total</option>
                  </select>
                </Field>
                <Field>
                  <label>Moeda</label>
                  <select value={moedaAnuncio} onChange={(e) => setMoedaAnuncio(e.target.value)}>
                    {moedas.length === 0 && <option value={moedaAnuncio}>{moedaAnuncio}</option>}
                    {moedas.map((m) => (
                      <option key={m.codigoMOEDA} value={m.isoMOEDA}>
                        {m.isoMOEDA} — {m.simboloMOEDA || m.tipoMOEDA || m.nomeMOEDA}
                      </option>
                    ))}
                  </select>
                </Field>
              </FieldGrid>
              {dados?.price?.approximate && (
                <WarnBox style={{ marginTop: 0, marginBottom: 10 }}>
                  Preço estimado para {fmtData(dados.raw?.checkIn)}–{fmtData(dados.raw?.checkOut)} — informe as datas da estadia para o valor exato.
                </WarnBox>
              )}
              <FieldGrid className="cols3">
                {periodicidade === 'noite' && (
                  <>
                    <Field>
                      <label>Valor total da estadia ({simboloMoeda})</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={valorTotal}
                        onChange={(e) => {
                          setValorTotal(e.target.value);
                          recalcDiaria(e.target.value, noites);
                        }}
                      />
                    </Field>
                    <Field>
                      <label>Quantidade de noites</label>
                      <input
                        type="number"
                        min="1"
                        value={noites}
                        onChange={(e) => {
                          setNoites(e.target.value);
                          recalcDiaria(valorTotal, e.target.value);
                        }}
                      />
                    </Field>
                  </>
                )}
                <Field $highlight={requiresReview}>
                  <label className={requiresReview ? 'required' : ''}>
                    {periodicidade === 'noite' ? `Valor da diária (${simboloMoeda})`
                      : periodicidade === 'mês' ? `Valor mensal (${simboloMoeda})`
                      : `Valor (${simboloMoeda})`}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={valorNoite}
                    onChange={(e) => setValorNoite(e.target.value)}
                  />
                </Field>
              </FieldGrid>
              <SummaryRow style={{ marginTop: 10 }}>
                {periodicidade === 'noite' && valorTotal && <span>Total: <b>{fmtMoney(valorTotal, moedaAnuncio)}</b></span>}
                {periodicidade === 'noite' && noites && <span>{noites} noite(s)</span>}
                {valorNoite && (
                  <span>
                    {periodicidade === 'noite' ? 'Diária média' : periodicidade === 'mês' ? 'Por mês' : 'Valor'}:{' '}
                    <b>{fmtMoney(valorNoite, moedaAnuncio)}</b>
                  </span>
                )}
              </SummaryRow>
              {!precoOk && (
                <WarnBox>
                  {periodicidade === 'noite'
                    ? 'Informe o valor total ou o valor da diária para continuar.'
                    : 'Informe o valor do anúncio para continuar.'}
                </WarnBox>
              )}
            </Section>

            {error && <ErrorBox>{error}</ErrorBox>}
            <ButtonRow>
              <GhostButton onClick={() => { setStep('form'); setDados(null); setError(''); }}>
                Voltar
              </GhostButton>
              <GoldButton onClick={confirmar} disabled={!precoOk}>
                Confirmar e publicar
              </GoldButton>
            </ButtonRow>
          </>
        )}

        {step === 'done' && result && (
          <>
            <ResultBox>
              <div className="ok-icon">✅</div>
              <h4 style={{ color: '#34574a', margin: '0 0 8px' }}>Anúncio importado com sucesso</h4>
              <p>
                Imóvel: <code>{result.imovel?.codigoIMOVEL}</code>
                <br />
                Anfitrião: {result.agente?.nomeAGENTE || '—'} <code>#{result.agente?.codigoAGENTE}</code>{result.agente?.reutilizado ? ' (reutilizado)' : ''}
                <br />
                Imobiliária: {result.imobiliaria?.nomeIMOBILIARIA || '—'} <code>#{result.imobiliaria?.codigoIMOBILIARIA}</code>{result.imobiliaria?.reutilizada ? ' (reutilizada)' : ''}
                <br />
                Fotos importadas: {result.fotosImportadas}
                <br />
                Fotos com falha: {result.fotosFalhas || 0}
              </p>
              {(result.fotosFalhas || 0) > 0 && (
                <WarnBox style={{ textAlign: 'left' }}>
                  {result.fotosFalhas} foto(s) não puderam ser enviadas — o anúncio foi criado mesmo assim.
                </WarnBox>
              )}
            </ResultBox>
            <ButtonRow style={{ justifyContent: 'center' }}>
              <GhostButton onClick={onClose}>Fechar</GhostButton>
              <PrimaryButton onClick={verAnuncio}>Ver anúncio</PrimaryButton>
            </ButtonRow>
          </>
        )}

        {step === 'duplicate' && (
          <>
            <ResultBox>
              <div className="ok-icon">⚠️</div>
              <h4 style={{ color: '#ba824f', margin: '0 0 8px' }}>Anúncio já importado</h4>
              <p>
                Este anúncio já existe no sistema
                {duplicateInfo?.codigoIMOVEL && <> como o imóvel <code>#{duplicateInfo.codigoIMOVEL}</code></>}.
              </p>
            </ResultBox>
            <ButtonRow style={{ justifyContent: 'center' }}>
              <GhostButton onClick={() => { setStep('form'); setDados(null); }}>Importar outro</GhostButton>
              {duplicateInfo?.codigoIMOVEL && <PrimaryButton onClick={verAnuncio}>Ver anúncio</PrimaryButton>}
            </ButtonRow>
          </>
        )}
      </ModalBox>
    </Overlay>
  );
};

export default ImportListingModal;
